import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled, css } from 'styled-components';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';

const requiredStyle = css`
  &::after {
    color: var(--wui-color-text-error);
    display: inline;
    padding-left: var(--wui-space-01);
    content: '*';
  }
`;

const disabledStyle = css`
  color: var(--wui-color-text-disabled);
`;

export type StyledLabelProps = Pick<ComponentPropsWithoutRef<'label'>, 'htmlFor'> & {
  $disabled: boolean;
  $required: boolean;
  $screenReaderOnly: boolean;
};

const StyledLabel = styled.label<StyledLabelProps>`
  display: block;
  width: 100%;
  color: var(--wui-color-text);
  font-family: var(--wui-typography-heading-6-family);
  line-height: var(--wui-typography-heading-6-line-height);
  font-size: var(--wui-typography-heading-6-size);
  font-weight: var(--wui-typography-heading-6-weight);

  /* if label is wrapping an input this ensures it appears beneath the label with a nice margin */
  > input,
  > textarea,
  > select {
    display: block;
  }

  > label {
    font-weight: normal;
  }

  ${({ $screenReaderOnly }) => $screenReaderOnly && visuallyHiddenStyle}
  ${({ $disabled }) => $disabled && disabledStyle}
  ${({ $required }) => $required && requiredStyle}
`;

export type LabelProps = ComponentPropsWithoutRef<'label'> & {
  /**
   * Pass an arbitrary child node
   */
  children?: ReactNode;
  /**
   * To indicate that the label is for a disabled form component
   */
  disabled?: boolean;
  /**
   * ID of the form component that the label should be associated with
   */
  htmlFor?: string | undefined;
  /**
   * Use if label describes a required form component
   */
  required?: boolean;
  /**
   * For label text that needs to be available to assistive technology (e.g. screen readers)
   * but otherwise hidden on screen.
   */
  screenReaderOnly?: boolean;
};

/**
 * Labels are used to give a title to a form component (eg. Input, Select, etc.)
 */
export const Label = ({
  children,
  disabled = false,
  htmlFor,
  required = false,
  screenReaderOnly = false,
  ...props
}: LabelProps): JSX.Element => {
  return (
    <StyledLabel
      {...props}
      $disabled={disabled}
      $required={required}
      $screenReaderOnly={screenReaderOnly}
      htmlFor={htmlFor}
    >
      {children}
    </StyledLabel>
  );
};

Label.displayName = 'Label';
